// Angular
import { AfterContentChecked, ChangeDetectorRef, Component, DOCUMENT, ElementRef, inject, Renderer2, RendererFactory2, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

// 3rdparty
import dayjs from 'dayjs';
import { finalize, map, Observable, startWith } from 'rxjs';

// Hostware
//import { RequestResult } from '../../interfaces/request-result';
import { SessionStorageService } from '../../services/session-storage.service';
import HwUtil from '../../utils/hw-util';

import { environment } from '../../../environments/environment';
import { CustomCalendarHeaderComponent } from '../../components/custom-calendar-header/custom-calendar-header.component';
import { LanguagesTwoDigit } from '../../consts/languages';
import { Regexes } from '../../consts/regexes';
import { Steps } from '../../consts/steps';
import { PreventBrowserBackButton } from '../../utils/prevent-browser-back-button';
import { AccessibilityService } from '../../services/accessibility.service';
import { PageBase } from '../page.base';
import { AreaResponse } from '../../dtos/area.response.dto';
import { BasketItemRequest } from '../../dtos/basket-item.request.dto';
import { BasketResponse } from '../../dtos/basket.response.dto';
import { CheckBasketRequest, Name, Customer, Address, TaxInfo } from '../../dtos/check-basket.request.dto';
import { CheckBasketResponse } from '../../dtos/check-basket.response.dto';
import { CheckInvoiceResponse } from '../../dtos/check-invoice.response.dto';
import { CityZipResponse } from '../../dtos/city-zip.response.dto';
import { CountryResponse } from '../../dtos/country.response.dto';
import { ImageResponse } from '../../dtos/image.response.dto';
import { InitPaymentRequest } from '../../dtos/init-payment.request.dto';
import { InitPaymentResponse } from '../../dtos/init-payment.response.dto';
import { LanguageSelectorResponse } from '../../dtos/language-selector.response.dto';
import { PaymentProverResponse } from '../../dtos/payment-provider.response.dto';
import { SubscriberInfoResponse } from '../../dtos/subscriber-info.response.dto';
import { TemplateInfoResponse } from '../../dtos/template-info.response.dto';
import { TicketGroupResponse } from '../../dtos/ticket-group.response.dto';
import { TicketResponse } from '../../dtos/ticket.response.dto';
import { BasketOperationMode } from '../../enums/basket-operation-mode.enum';
import { TicketModel } from '../../models/ticket.model';
import { UrlParam } from '../../models/url-param';
import { KeyValuePair, UserDataModel } from '../../models/user-data.model';
import { BasketService } from '../../services/basket.service';
import { Error } from '../../models/error';

@Component({
    standalone: false,
    selector: 'landing-page',
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent extends PageBase implements AfterContentChecked {
    dateAdapter: DateAdapter<Date> = inject(DateAdapter<Date>)
    breakPointObserver: BreakpointObserver = inject(BreakpointObserver)
    changeDetector = inject(ChangeDetectorRef)
    sessionStorageService = inject(SessionStorageService)
    basketService = inject(BasketService)
    preventBrowserBackButton = inject(PreventBrowserBackButton)
    accessibilityService = inject(AccessibilityService)
    document = inject(DOCUMENT);

    private renderer: Renderer2;

    @ViewChild('wrapper') private scrollContainer: ElementRef;

    activeStep: number = 0;
    actIndex: number = 0;
    maxSteps: number;

    //dateFormatOptions: { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };

    selectedDate: Date;
    minDate: Date;
    maxDate: Date;
    today = new Date();

    dateSelectionCtl: FormControl;
    areaSelectionCtl = new FormControl();

    personalDataForm: FormGroup;
    summaryForm: FormGroup;
    loading: boolean;
    imagesLoaded: boolean;

    areaList: AreaResponse[] = [];
    groupList: TicketGroupResponse[] = [];
    ticketList: TicketModel[] = [];

    steps: Step[] = [];
    columns: number = 1;
    logoPath: string;
    subTotal: number;
    isMobile: boolean;
    isTablet: boolean;
    basketMode: string;
    loaded: boolean = false;
    currentState: BreakpointState;
    onlineSaleForTransactionDay: boolean;
    BasketOpMode: BasketOperationMode;
    customCalendarHeader = CustomCalendarHeaderComponent;

    apiUrlBase = environment.apiServer;

    subscriberInfo: SubscriberInfoResponse;
    paymentOptions: PaymentProverResponse[];
    szepPaymentOptions: PaymentProverResponse[];
    cardPaymentOptions: PaymentProverResponse[];
    countries: CountryResponse[];
    filteredCountries: Observable<CountryResponse[]>;
    cityZips: CityZipResponse[];
    filteredCityZips: Observable<CityZipResponse[]>;
    countryCodeFormControl: FormControl;
    cityZipFormControl: FormControl;
    closedForToday: boolean;

    checkBasketRequest: CheckBasketRequest;
    languageSelectorItems: LanguageSelectorResponse[];
    imagesInfo: ImageResponse[];
    conditions: TemplateInfoResponse[];
    settings: Object;

    selectedLanguageCode: string;
    ticketSalesForActualDaySetting: any;
    personalDataFormSettings = {};

    selectedAreaId: string;
    selectedGroupId: string;
    canUserBuyTicketForToday: boolean;

    useAreas: boolean;
    useGroups: boolean;

    sptw1: number = 30;
    sptw2: number = 70;
    sptw1_xs: number = 100;
    sptw2_xs: number = 100;

    isAccesibilityMode: boolean = false;

    constructor(rendererFactory: RendererFactory2) {
        super();
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    toggleAccessibilityButton() {
        this.accessibilityService.toggle();
    }

    async ngOnInit() {
        // ennek itt kell lennie
        this.preventBrowserBackButton.register(() => this.previousStep());

        const today = new Date();

        const sessionStorageUrlParams: UrlParam = this.sessionStorageService.getUrlParams();
        if (!sessionStorageUrlParams) {
            this.errorService.showErrorText(this.translateService.instant("errors.ERR_LAUNCH_APP_FROM_DEALER_SITE"));
            return;
        }

        this.showSpinner();

        this.selectedLanguageCode = sessionStorageUrlParams.Language;

        // initialize translation
        //await this.t.initialize(sessionStorageUrlParams.Language);

        this.basketService.basketValidationError$.subscribe(event => {
            if (this.activeStep === Steps.TicketSelection) {
                this.activeStep = Steps.ShowBasket;
                this.errorService.showErrorText(this.t.errors("ERR_BASKET"));
            }
        })

        //this.activeStep = Steps.DateSelection;

        this.steps.push({ StepIndex: 0, StepName: this.t.steps("date_selection_header"), LogicalIndex: 1 });
        this.steps.push({ StepIndex: 1, StepName: this.t.steps("area_selection_header"), LogicalIndex: 2 });
        this.steps.push({ StepIndex: 2, StepName: this.t.steps("group_selection_header"), LogicalIndex: 3 });
        this.steps.push({ StepIndex: 3, StepName: this.t.steps("ticket_selection_header"), LogicalIndex: 4 });
        this.steps.push({ StepIndex: 4, StepName: this.t.steps("show_basket_header"), LogicalIndex: 5 });
        this.steps.push({ StepIndex: 5, StepName: this.t.steps("invoice_data_header"), LogicalIndex: 6 });
        this.steps.push({ StepIndex: 6, StepName: this.t.steps("check_summary_header"), LogicalIndex: 7 });

        this.maxSteps = this.steps[this.steps.length - 1].LogicalIndex;

        this.dateAdapter.setLocale(sessionStorageUrlParams.Language);

        this.breakPointObserver.observe([Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge
        ]).subscribe((state: BreakpointState) => {
            this.currentState = state;
            this.isMobile = false;
            this.isTablet = false;
            if (state.breakpoints[Breakpoints.XSmall] || state.breakpoints[Breakpoints.Small]) {
                this.isMobile = true;
            }
            else if (state.breakpoints[Breakpoints.Medium]) {
                this.isTablet = true;
            }
        })

        this.loadInitialData();
    }

    ngAfterContentChecked(): void {
        this.changeDetector.detectChanges();
    }

    loadInitialData() {
        this.appInit.whenReady()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.hideSpinner();
                })
            )
            .subscribe({
                next: async () => {
                    const initData = this.appInit.initData;
                    if (!initData)
                        return;

                    this.paymentOptions = initData.paymentProviders;
                    this.szepPaymentOptions = initData.paymentProviders.filter(x => x.cardType === "SZEP");
                    this.cardPaymentOptions = initData.paymentProviders.filter(x => x.cardType === "CREDIT");
                    this.countries = initData.countries;
                    this.subscriberInfo = initData.subscriber;
                    this.languageSelectorItems = initData.languages;
                    this.imagesInfo = initData.images;
                    this.conditions = initData.conditions;
                    this.settings = initData.settings;
                    this.cityZips = initData.cityZips;
                    this.cityZips.forEach(x => x.text = x.zip + " " + x.city);
                    this.closedForToday = initData.closedForToday;

                    if (initData.returnUrl)
                        this.sessionStorageService.setReturnUrl(initData.returnUrl);

                    this.setForms();

                    this.filteredCountries = this.countryCodeFormControl.valueChanges.pipe(
                        startWith(''),
                        map((value: any) => {
                            const name: string = typeof value === 'string' ? value : value?.commonName;
                            return name ? this.filterCountries(name) : this.countries.slice();
                        }),
                    );

                    this.filteredCityZips = this.cityZipFormControl.valueChanges.pipe(
                        startWith(''),
                        map((value: any) => {
                            const name: string = typeof value === 'string' ? value : value?.text;
                            return name ? this.filterCityZips(name) : this.cityZips.slice();
                        }),
                    );

                    this.logoPath = this.getImageUrlByType("IMAGE_LOGO");

                    this.useAreas = this.getSettingValue("ShowAreas");
                    this.useGroups = this.getSettingValue("ShowTicketGroups");

                    this.setDatePickerRestrictions();

                    const userData: UserDataModel = this.loadUserData();
                    if (userData) {
                        if (this.useAreas)
                            await this.LoadAreaList(this.selectedDate);

                        if (this.useGroups)
                            await this.LoadGroupList();

                        await this.LoadTicketList();

                        this.setStep(userData.stepIndex);
                    }

                    this.dateSelectionCtl = new FormControl({ disabled: true, value: this.selectedDate });

                    this.requestFinished();
                    this.loaded = true;
                },
                error: (err) => this.errorService.showError(err)
            })
    }

    setForms() {
        this.personalDataFormSettings = {
            FirstName: { maxLength: 45 },
            LastName: { maxLength: 45 },
            City: { maxLength: 50 },
            ZipCode: { maxLength: 10 },
            StreetAndNumber: { maxLength: 50 },
            CompanyName: { maxLength: 90 },
            TaxNumber: { maxLength: 50 },
            EuTaxNumber: { maxLength: 50 },
            Email: { maxLength: 60 },
            ConfirmEmail: { maxLength: 60 },
            PhoneNumber: { maxLength: 18 }
        };

        this.countryCodeFormControl = new FormControl<CountryResponse>(null, [Validators.required, autocompleteValueValidator(this.countries.map(x => x.iso2Code))]);
        this.cityZipFormControl = new FormControl<CityZipResponse>(null, [Validators.required, autocompleteValueValidator(this.cityZips.map(x => x.text))]);

        this.personalDataForm = this.fb.group({
            FirstName: new FormControl(null, [Validators.required, Validators.pattern(Regexes.firstNameLastName), Validators.maxLength(this.personalDataFormSettings['FirstName'].maxLength)]),
            LastName: new FormControl(null, [Validators.required, Validators.pattern(Regexes.firstNameLastName), Validators.maxLength(this.personalDataFormSettings['LastName'].maxLength)]),
            CountryCode: this.countryCodeFormControl,
            CityZip: this.cityZipFormControl,
            ZipCode: new FormControl(null),
            City: new FormControl(null),
            StreetAndNumber: new FormControl(null, [Validators.required, Validators.pattern(Regexes.address), Validators.maxLength(this.personalDataFormSettings['StreetAndNumber'].maxLength)]),
            PhoneNumber: new FormControl(null, [Validators.pattern(Regexes.phoneNumber), Validators.maxLength(this.personalDataFormSettings['PhoneNumber'].maxLength)]),
            Email: new FormControl(null, [Validators.required, Validators.email, Validators.maxLength(this.personalDataFormSettings['Email'].maxLength)]),
            ConfirmEmail: new FormControl(null, [Validators.required, Validators.email, Validators.maxLength(this.personalDataFormSettings['ConfirmEmail'].maxLength), emailMatchValidator(() => (this.getFormValue('Email')))]),
            PrivatePerson: new FormControl(true),
            CompanyName: new FormControl(null),
            TaxNumber: new FormControl(null),
            EuTaxNumber: new FormControl(null, [Validators.maxLength(this.personalDataFormSettings['EuTaxNumber'].maxLength)]),
            PaymentOptionCode: new FormControl(null, [Validators.required])
        })

        // set taxnumber and companyName validation by privatePerson checkbox
        this.personalDataForm.get('PrivatePerson').valueChanges.subscribe(val => {
            if (!this.getFormValue('PrivatePerson')) {
                this.personalDataForm.controls['TaxNumber'].addValidators([Validators.required, Validators.pattern(Regexes.taxnumber), Validators.maxLength(this.personalDataFormSettings['TaxNumber'].maxLength)]);
                this.personalDataForm.controls['CompanyName'].addValidators([Validators.required, Validators.maxLength(this.personalDataFormSettings['CompanyName'].maxLength)]);
            } else {
                this.personalDataForm.controls['TaxNumber'].clearValidators();
                this.personalDataForm.controls['CompanyName'].clearValidators();
            }

            this.personalDataForm.controls['TaxNumber'].updateValueAndValidity();
            this.personalDataForm.controls['CompanyName'].updateValueAndValidity();
        });

        // set zip, city validator
        this.personalDataForm.get('CountryCode').valueChanges.subscribe(val => {
            if (this.isHungarySelected()) {
                this.personalDataForm.controls['CityZip'].addValidators(Validators.required);
                this.personalDataForm.controls['ZipCode'].clearValidators();
                this.personalDataForm.controls['City'].clearValidators();
            } else {
                this.personalDataForm.controls['CityZip'].clearValidators();
                this.personalDataForm.controls['ZipCode'].addValidators([Validators.required, Validators.pattern(Regexes.zip), Validators.maxLength(this.personalDataFormSettings['ZipCode'].maxLength)]);
                this.personalDataForm.controls['City'].addValidators([Validators.required, Validators.pattern(Regexes.city), Validators.maxLength(this.personalDataFormSettings['City'].maxLength)]);
            }

            this.personalDataForm.controls['CityZip'].updateValueAndValidity();
            this.personalDataForm.controls['ZipCode'].updateValueAndValidity();
            this.personalDataForm.controls['City'].updateValueAndValidity();
        });

        this.summaryForm = this.fb.group({
            acceptPrivacyPolicy: new FormControl(null, [Validators.requiredTrue]),
            acceptAFSZ: new FormControl(null, [Validators.requiredTrue]),
            acceptOTPSimple: new FormControl(null)
        });

        this.personalDataForm.get('PaymentOptionCode')?.valueChanges.subscribe((paymentOption: string) => {
            this.updateOTPSimpleValidation(paymentOption);
        });

        this.updateOTPSimpleValidation(this.personalDataForm.get('PaymentOptionCode')?.value);
    }

    private updateOTPSimpleValidation(paymentOption: string | null | undefined): void {
        const otpControl = this.summaryForm.get('acceptOTPSimple');

        if (paymentOption === 'OTPSimple') {
            otpControl?.setValidators([Validators.requiredTrue]);
        } else {
            otpControl?.clearValidators();
        }

        otpControl?.updateValueAndValidity();
    }

    public isHungarySelected() {
        return this.getFormValue('CountryCode') && this.getFormValue('CountryCode').toLowerCase() === LanguagesTwoDigit.HU;
    }

    private filterCountries(commonName: string): CountryResponse[] {
        const filterValue = commonName.toLowerCase();
        return this.countries.filter(country =>
            country.commonName.toLowerCase().includes(filterValue) ||
            country.name.toLowerCase().includes(filterValue) ||
            country.officialName.toLowerCase().includes(filterValue)
        );
    }

    public displayCountriesDropdown(iso2Code: string): string {
        let result: CountryResponse[] = this.countries.filter(x => x.iso2Code == iso2Code);
        if (!result || result.length == 0)
            return "";

        return result[0].commonName;
    }

    private filterCityZips(cityZipName: string): CityZipResponse[] {
        const filterValue = cityZipName.toLowerCase();
        return this.cityZips.filter(cityZip => cityZip.text.toLowerCase().includes(filterValue));
    }



    setDatePickerRestrictions() {
        let now = new Date();

        // set MaxDate
        let maxDate: Date = now;
        const ticketBookingBeforeDays: number = this.getSettingValue("TicketBookingBeforeDays");
        if (ticketBookingBeforeDays > 0)
            maxDate.setDate(now.getDate() + ticketBookingBeforeDays);
        else
            maxDate = null;

        this.maxDate = HwUtil.ConvertDateToDateOnly(maxDate);


        // set MinDate
        now = new Date();
        const minDate: Date = new Date();

        this.ticketSalesForActualDaySetting = this.getSettingValue("TicketSalesForActualDay");
        // if opened for today
        if (!this.closedForToday) {
            // mára lehetséges venni
            if (this.ticketSalesForActualDaySetting.available) {
                this.canUserBuyTicketForToday = true;
                if (this.ticketSalesForActualDaySetting.to) {
                    var arr = this.ticketSalesForActualDaySetting.to.split(/[- :]/);
                    const dateToCompare: Date = new Date(now.getFullYear(), (now.getMonth() - 1), now.getDate(), arr[0], arr[1], arr[2]);
                    if (dateToCompare < new Date(now.getFullYear(), (now.getMonth() - 1), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds())) // ha most már több az idő mint amíg lehet jegyet venni mára
                        minDate.setDate(new Date().getDate() + 1);  // már csak holnapra lehet jegyet venni
                }
            }
            // mára nem lehetséges
            else {
                this.canUserBuyTicketForToday = false;
                minDate.setDate(new Date().getDate() + 1);
            }
        }
        // if closed for today
        else {
            this.canUserBuyTicketForToday = false;
            minDate.setDate(new Date().getDate() + 1);
        }

        this.minDate = HwUtil.ConvertDateToDateOnly(minDate);
        this.selectedDate = this.minDate;
    }

    getbookingHoldTimeForCapacityCheckWarningText(): string {
        var arr = this.getSettingValue("BookingHoldTimeForCapacityCheck").split(/[- :]/);

        return this.replaceValueInText(
            this.t.summaryForm("bookingTimeWarning"),
            "{bookingHoldTimeForCapacityCheckWarning}",
            new Date(1, 1, 1, arr[0], arr[1], arr[2]).getMinutes()
        );
    }

    getImageUrlByType(imageType: string) {
        return this.getImageUrl(this.imagesInfo.find(x => x.imageType == imageType)?.id);
    }

    getImageUrl(imageId?: string): string {
        if (imageId === undefined)
            return "";

        return `${this.apiUrlBase}/subscriber/image/${imageId}`;
    }

    getSettingValue(settingKey: string): any {
        if (this.settings.hasOwnProperty(settingKey))
            return this.settings[settingKey].value;

        return undefined;
    }

    selectDateChanged(event: Date) {
        this.selectedDate = event;
    }

    addToBasket(item: TicketModel) {
        if (item.quantity > 0) {

            let basketItemRequest: BasketItemRequest = new BasketItemRequest();
            basketItemRequest.areaId = item.areaId;
            basketItemRequest.ticketId = item.ticketId;
            basketItemRequest.date = HwUtil.DateToISOString(this.selectedDate);
            basketItemRequest.quantity = item.quantity;

            this.basketService.incrementQuantity(basketItemRequest.ticketId, basketItemRequest.date, basketItemRequest.quantity, basketItemRequest.areaId);
            item.quantity = 1;
        }
    }

    increment(ticket: TicketModel) {
        ticket.quantity = ticket.quantity + 1;
    }

    decrement(ticket: TicketModel) {
        if (ticket.quantity > 1) {
            ticket.quantity = ticket.quantity - 1;
        }
    }

    checkPersonalDataFormHasMaxLengthError(formFieldName: string) {
        return this.checkFormHasMaxLengthError(this.personalDataForm, formFieldName)
    }

    getPersonalDataFormMaxLengthErrorMessage(formFieldName: string) {
        return this.getMaxLengthErrorMessage(this.personalDataForm, formFieldName);
    }

    getPersonalDataFormMaxLength(formFieldName: string): string {
        return this.personalDataFormSettings[formFieldName].maxLength;
    }

    checkPersonalDataFormHasRequiredError(formFieldName: string) {
        return this.checkFormHasRequiredError(this.personalDataForm, formFieldName)
    }

    checkPersonalDataFormHasServerError(formFieldName: string): boolean {
        return this.checkFormHasServerError(this.personalDataForm, formFieldName);
    }

    getPersonalDataFormServerError(fieldName: string): string {
        return this.getServerError(fieldName);
    }

    checkPersonalDataFormHasPatternError(formFieldName: string): boolean {
        return this.checkFormHasPatternError(this.personalDataForm, formFieldName)
    }

    checkPersonalDataFormHasEmailError(fieldName: string): boolean {
        return this.checkFormHasEmailError(this.personalDataForm, fieldName);
    }

    checkAutocompleteValueIsValid(formFieldName: string): boolean {
        if (!this.checkFormFieldIsExists(this.personalDataForm, formFieldName))
            return true;

        return this.personalDataForm.controls[formFieldName].hasError('autocompleteValueValidationError');
    }

    checkPersonalDataFormIsEmailMatch(confirmEmailFormFieldName: string): boolean {
        return this.personalDataForm.controls[confirmEmailFormFieldName].hasError('emailMatch') && this.personalDataForm.controls[confirmEmailFormFieldName].touched;
    }
    // Validator helper functions ---------------

    LoadAreaList(dateToSelect: Date) {
        this.areaList = [];

        this.showSpinnerWithDelay();

        return new Promise((resolve) => {
            this.shopService.getAllAreas(dateToSelect)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.hideSpinner();
                    })
                )
                .subscribe({
                    next: (result) => {
                        this.requestFinished();
                        this.areaList = result as AreaResponse[];

                        if (!this.areaList || this.areaList.length == 0)
                            this.errorService.showErrorText(this.t.errors("ERR_NO_AREA"));

                        resolve('resolved');
                    },
                    error: (err) => this.errorService.showError(err)
                })
        });
    }

    LoadGroupList() {
        this.groupList = [];
        this.showSpinnerWithDelay();

        return new Promise((resolve) => {
            this.shopService.getAllGroups(this.selectedDate, this.selectedAreaId)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.hideSpinner();
                    })
                )
                .subscribe({
                    next: (result) => {
                        this.requestFinished();
                        this.groupList = result as TicketGroupResponse[];

                        resolve('resolved');
                    },
                    error: (err) => this.errorService.showError(err)
                })
        });
    }

    LoadTicketList() {
        this.ticketList = [];
        this.showSpinnerWithDelay();

        return new Promise((resolve) => {
            this.shopService.getTickets(this.selectedDate, this.selectedAreaId, this.selectedGroupId)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    finalize(() => {
                        this.hideSpinner();
                    })
                )
                .subscribe({
                    next: (result) => {
                        this.requestFinished();
                        this.ticketList = result as TicketModel[];

                        this.ticketList.forEach((ticket) => {
                            ticket.quantity = 1;
                            ticket.areaId = this.selectedAreaId;
                        });

                        resolve('resolved');

                        if (!this.ticketList || this.ticketList.length == 0)
                            this.errorService.showErrorText(this.t.errors("ERR_NO_TICKET"));
                    },
                    error: (error) => this.errorService.showError(error)
                });
        });
    }

    dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
        const today = dayjs(dayjs().format("YYYY.MM.DD"))
        if (dayjs(dayjs.unix(cellDate.getTime() / 1000).format("YYYY.MM.DD")).isBefore(today)) {
            return 'disabled-date';
        }
    }

    truncateString(text: string, length: number): string {
        return HwUtil.truncateString(text, length);
    }

    ticketsCount(): number {
        let sum: number = 0;
        this.basketService.basketRequest.forEach(x => sum += x.quantity);
        return sum;
    }

    getBasketBadgeCssClass() {
        if (!this.basketService.isAnyItemInBasket())
            return "basket";

        return this.basketService.isBasketValid() ? "basket hw-cursor" : "basket-error hw-cursor";
    }

    // dynamic styling functions
    basketStyle(mobile: boolean) {
        let styles = {};
        if (this.isMobile) {
            styles["z-index"] = "0";
            styles["position"] = "relative"
            styles["width"] = "100%";
            styles["top"] = "0px";
        } else {
            styles["position"] = "fixed";
            styles["width"] = "300px";
            styles["top"] = "14px";
            styles["right"] = "30px";
            styles["z-index"] = "5000";
        }
        return styles;
    }

    leftSideFlex(): string {
        if (this.isMobile) {
            return "100";
        }
        else {
            return "50"
        }
    }

    headerLeftFx(): string {
        if (this.currentState.breakpoints[Breakpoints.XSmall] || this.currentState.breakpoints[Breakpoints.XSmall]) {
            return "90";
        }
        else {
            return "100"
        }
    }

    selectDateClicked: boolean;
    // Paging ----------------
    // from Page 1(Date selection) to 2(area selection)
    async selectDate() {
        const resource = this.translateService.instant("shopping.date_selection_question");
        let questionText: string = this.replaceValueInText(
            resource,
            "{selectedDay}",
            `<span class='hw-font-size-18px hw-font-weight-700 primary-color'>${formatDate(this.selectedDate, 'shortDate', this.currLocale)}</span>`
        );
        
        questionText = this.replaceValueInText(
            questionText,
            "{0}",
            "<span class='hw-font-weight-700'>")
        
        questionText = this.replaceValueInText(
            questionText,
            "{1}",
            "</span>")
        
        const yesNoDialogResult = await this.dialogService.openYesNoDialog(questionText);

        if (yesNoDialogResult) {
            if (!this.selectDateClicked)
                history.pushState(null, '', location.href);

            this.selectDateClicked = true;

            if (this.useAreas) {
                await this.LoadAreaList(this.selectedDate);

                if (this.areaList.length === 0) {
                    // nem történik semmi
                }
                else if (this.areaList.length === 1) {
                    this.selectedAreaId = this.areaList[0].areaId;
                    await this.selectArea(this.selectedAreaId);
                }
                else if (this.areaList.length > 1) {
                    this.setStep(Steps.AreaSelection);
                }
            }
            else {
                await this.selectArea();
            }
        }
    }

    // from Page 2(area selection) to 3(group selection)
    async selectArea(areaId?: string) {
        this.selectedAreaId = areaId;

        if (this.useGroups) {
            await this.LoadGroupList();
            if (this.groupList.length === 0) {
                this.selectGroup();
            }
            else if (this.groupList.length === 1) {
                this.selectedGroupId = this.groupList[0].ticketGroupId;
                this.selectGroup(this.selectedGroupId);
            }
            else if (this.groupList.length > 1)
                this.setStep(Steps.GroupSelection);
        }
        else {
            this.selectGroup();
        }
    }

    // from Page 3(group selection) to 4(ticket add to basket)
    async selectGroup(groupId?: string) {
        this.selectedGroupId = groupId;
        await this.LoadTicketList();
        if (this.ticketList && this.ticketList.length > 0) {
            this.setStep(Steps.TicketSelection);
        }
    }

    // from Page 4(ticket add to basket) to 5(show basket)
    goToBasket() {
        this.setStep(Steps.ShowBasket);
    }

    // from Page 5(show basket) to 4(ticket add to basket) or to 0(date selection)
    async backFromBasket() {
        if (this.ticketList && this.ticketList.length > 0) {
            this.setStep(Steps.TicketSelection);
            return;
        }

        this.backFromTicket();
    }

    backFromTicket() {
        if (this.useGroups) {
            if (this.groupList.length === 0)
                this.backFromGroup();
            if (this.groupList.length === 1)
                this.backFromGroup();
            else if (this.groupList.length > 1)
                this.setStep(Steps.GroupSelection);
        }
        else {
            this.backFromGroup();
        }
    }

    backFromGroup() {
        if (this.useAreas) {
            if (this.areaList.length === 0)
                this.setStep(Steps.DateSelection);
            if (this.areaList.length === 1)
                this.setStep(Steps.DateSelection);
            else if (this.areaList.length > 1)
                this.setStep(Steps.AreaSelection);
        }
        else {
            this.setStep(Steps.DateSelection);
        }
    }

    handleAreas(): boolean {
        return this.useAreas && this.areaList.length > 1;
    }

    handleGroups(): boolean {
        return this.useGroups && this.groupList.length > 1;
    }

    // from Page 4(show basket) to 5(invoice data)
    gotoInvoiceData() {
        this.showSpinnerWithDelay();

        this.shopService.getBasketPrices(this.basketService.basketRequest)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.hideSpinner();
                })
            )
            .subscribe({
                next: (response) => {
                    this.requestFinished();
                    let basketResponse = response as BasketResponse;

                    if (!basketResponse.hasError) {
                        this.setStep(Steps.InvoiceData, false);
                    }
                    else {
                        this.setStep(Steps.ShowBasket);
                        this.errorService.showErrorText(this.t.errors("ERR_BASKET"));
                    }
                },
                error: (err) => this.errorService.showError(err)
            })
    }

    // from Page 5(invoice data) to 6(summary)
    gotoSummary() {
        if (!this.basketService.isAnyItemInBasket()) {
            this.errorService.showErrorText(this.t.errors("ERR_EMPTY_BASKET"));
            this.setStep(Steps.DateSelection);
            return;
        }

        if (this.isHungarySelected()) {
            let cityZip: CityZipResponse = this.cityZips.find(x => x.text === this.getFormValue("CityZip"));

            this.setFormValue("ZipCode", cityZip.zip);
            this.setFormValue("City", cityZip.city);
        }

        this.checkBasketRequest = this.createCheckBasketRequest();

        this.showSpinnerWithDelay();

        this.shopService.checkBasket(this.checkBasketRequest)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.hideSpinner();
                })
            )
            .subscribe({
                next: (result) => {
                    this.serverErrors = null;
                    this.requestFinished();
                    let checkBasketResult: CheckBasketResponse = result as CheckBasketResponse;

                    if (checkBasketResult.hasError) {
                        let dialogRef = this.errorService.showErrorText(this.t.errors("ERR_BASKET"));

                        dialogRef.afterClosed().subscribe(result => {
                            this.setStep(Steps.ShowBasket);
                        });
                    }
                    else {
                        this.setStep(Steps.Summary);
                    }
                },
                error: (err) => {
                    const errors: Error[] = err.error.errors as Error[];
                    this.serverErrors = {};
                    for (let ii = 0; ii < errors.length; ii++) {
                        const propertyName: string = HwUtil.getShortPropertyName(errors[ii].propertyName);

                        this.personalDataForm.controls[propertyName].setErrors({ 'serverError': true });
                        this.personalDataForm.controls[propertyName].markAsTouched();

                        if (!this.serverErrors[propertyName])
                            this.serverErrors[propertyName] = errors[ii].message;
                        else
                            this.serverErrors[propertyName] += errors[ii].message;
                    }

                    this.errorService.showError(err);
                }
            })
    }

    // from Page6 Execute payment
    gotoPayment() {
        if (!this.basketService.isAnyItemInBasket()) {
            this.errorService.showErrorText(this.t.errors("ERR_EMPTY_BASKET"));
            this.setStep(Steps.DateSelection);
            return;
        }

        if (!this.checkBasketRequest)
            this.checkBasketRequest = this.createCheckBasketRequest();

        this.executePayment();
    }

    previousStep() {
        switch (this.activeStep) {
            case Steps.ShowBasket:
                this.backFromBasket();
                break;

            case Steps.TicketSelection:
                this.backFromTicket();
                break;

            case Steps.GroupSelection:
                this.backFromGroup();
                break;

            case Steps.AreaSelection:
            case Steps.InvoiceData:
            case Steps.Summary:
                if (this.activeStep >= Steps.AreaSelection) {
                    this.setStep(this.activeStep - 1);
                }
                break;

            default: break;
        }
        this.scrollToTop();
    }
    // Paging ----------------

    stepName(): string {
        let item = this.steps.find(item => item.StepIndex == this.activeStep);
        if (item)
            return this.steps.find(item => item.StepIndex == this.activeStep).StepName;

        return "";
    }

    stepIndex(): number {
        let item = this.steps.find(item => item.StepIndex == this.activeStep);
        if (item)
            return item.LogicalIndex;

        return 0;
    }

    setStep(stepNumber: number, calculateBasket: boolean = true) {
        this.activeStep = stepNumber;

        if (calculateBasket)
            this.basketService.calculateBasket();

        this.scrollToTop();
    }

    scrollToTop() {
        setTimeout(() => {
            //window.scrollTo(0, 0);
            //document.getElementById('Pager').focus();
            document.getElementById('Pager').click();
        }, 100);
    }

    createCheckBasketRequest() {
        let name: Name = new Name(this.getFormValue("FirstName"), this.getFormValue("LastName"));
        let privatePerson: boolean = !!this.getFormValue("PrivatePerson");

        let customer: Customer = new Customer(
            privatePerson,
            name,
            new Address(this.getFormValue("CountryCode"), this.getFormValue("ZipCode"), this.getFormValue("City"), this.getFormValue("StreetAndNumber")),
            this.getFormValue("Email"),
            this.getFormValue("ConfirmEmail")
        );

        if (!privatePerson) {
            customer.taxInfo = new TaxInfo(this.getFormValue("TaxNumber"));

            if (this.getFormValue("EuTaxNumber"))
                customer.taxInfo.euTaxNumber = this.getFormValue("EuTaxNumber");

            customer.companyName = this.getFormValue("CompanyName");
        }

        if (this.getFormValue("PhoneNumber"))
            customer.phoneNumber = this.getFormValue("PhoneNumber");

        let checkBasketRequest: CheckBasketRequest = new CheckBasketRequest();
        checkBasketRequest.startDate = new Date();
        checkBasketRequest.items = this.basketService.getBasket();
        checkBasketRequest.customer = customer;
        checkBasketRequest.paymentProvider = this.getFormValue('PaymentOptionCode');

        return checkBasketRequest;
    }

    executePayment() {
        this.showSpinner();

        const checkBasket$ = this.shopService.checkInvoice(this.checkBasketRequest)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    let checkBasketResult: CheckInvoiceResponse = result as CheckInvoiceResponse;

                    // validation error
                    if (checkBasketResult.hasError) {
                        this.hideSpinnerWithSubscription(checkBasket$);
                        let dialogRef = this.errorService.showErrorText(this.t.errors("ERR_BASKET"));

                        dialogRef.afterClosed().subscribe(result => {
                            this.setStep(Steps.ShowBasket);
                        });
                    }
                    // no error
                    else {
                        let initPaymentRequest = new InitPaymentRequest(checkBasketResult.transactionId, this.checkBasketRequest.paymentProvider);
                        this.shopService.initPayment(initPaymentRequest)
                            .pipe(
                                takeUntilDestroyed(this.destroyRef),
                                finalize(() => {
                                    this.hideSpinner();
                                })
                            )
                            .subscribe({
                                next: (result) => {
                                    let initPaymentResponse: InitPaymentResponse = result as InitPaymentResponse;

                                    if (initPaymentResponse && initPaymentResponse.paymentLink) {
                                        this.saveUserData();
                                        HwUtil.redirectWithoutHistory(initPaymentResponse.paymentLink);
                                    }
                                },
                                error: (err) => this.errorService.showError(err)
                            })
                    }
                }, error: (err) => {
                    this.hideSpinnerInternal();
                    this.errorService.showError(err);
                }
            });
    }

    getConditionText(templateType: string) {
        let results: TemplateInfoResponse[] = this.conditions.filter(x => x.info.templateType.value === templateType);
        if (results && results.length > 0)
            return results[0].template;
    }

    basketIconVisible(): boolean {
        //if (this.activeStep < Steps.ShowBasket && this.basketService.basketRequest.length > 0) {
        return true;
        //} else {
        //    return false;
        //}
    }

    getFormValue(controlName: string) {
        return this.personalDataForm?.controls[controlName].value;
    }

    setFormValue(controlName: string, value: any) {
        return this.personalDataForm.controls[controlName].setValue(value);
    }

    getSelectedPaymentOptionLabel(): string {
        return this.paymentOptions.find(option => option.provider.value === this.getFormValue('PaymentOptionCode')).provider.name;
    }

    getAreaImagePath(area: AreaResponse): string {
        if (area.imageId)
            return this.getImageUrl(area.imageId);

        return HwUtil.areaKindIconPath(area.kind.value);
    }

    getGroupImagePath(group: TicketGroupResponse): string {
        if (group.imageId)
            return this.getImageUrl(group.imageId);

        return "";
    }

    getTicketImagePath(ticket: TicketResponse) {
        return this.getImageUrl(ticket.imageId);
    }

    loadControlsFromUserData(userData: UserDataModel) {
        if (userData) {
            for (var item in userData.customerData) {
                this.setFormValue(userData.customerData[item].key, userData.customerData[item].value);
            }
        }
    }

    loadUserData(): UserDataModel {
        const userData: UserDataModel = this.sessionStorageService.getUserData();

        if (userData) {
            const selectedDate: Date = new Date(userData.selectedDate);

            this.selectedAreaId = userData.selectedAreaId;
            this.selectedGroupId = userData.selectedGroupId;

            this.selectedDate = selectedDate;

            if (this.selectedDate < this.minDate)
                this.selectedDate = this.minDate;

            if (this.selectedDate > this.maxDate)
                this.selectedDate = this.maxDate;

            this.loadControlsFromUserData(userData);
        }

        return userData;
    }

    saveUserData() {
        let userData = new UserDataModel();
        userData.stepIndex = this.activeStep;

        let customers: KeyValuePair[] = [];
        for (var key in this.personalDataForm.controls) {
            customers.push(new KeyValuePair(key, this.personalDataForm.controls[key].value));
        }

        userData.customerData = customers;
        userData.selectedDate = this.selectedDate;
        userData.selectedAreaId = this.selectedAreaId;
        userData.selectedGroupId = this.selectedGroupId;

        this.sessionStorageService.saveUserData(userData);
    }

    changeLanguage(languageCode: string) {
        this.saveUserData();

        this.sessionStorageService.updateLanguageInUrlParams(languageCode);

        const sessionStorageUrlParams: UrlParam = this.sessionStorageService.getUrlParams();
        const changeLanguagePath = `${window.location.origin}/?Subscriber=${sessionStorageUrlParams.Subscriber}&HotelId=${sessionStorageUrlParams.HotelId}&Language=${languageCode}`;
        HwUtil.redirectWithoutHistory(changeLanguagePath);
    }

    async showLanguageSelector() {
        const choosableLanguages: LanguageSelectorResponse[] = this.languageSelectorItems.filter(x => x.code != this.selectedLanguageCode);
        const newLanguage = await this.dialogService.openLanguageSelectorDialog(choosableLanguages);
        if (newLanguage)
            this.changeLanguage(newLanguage as string);
    }

    getSelectedLanguageObject(): LanguageSelectorResponse {
        return this.languageSelectorItems.filter(x => x.code == this.selectedLanguageCode)[0];
    }

    showTooltip(text: string) {
        this.dialogService.showMessage(text);
    }

    override async backToDealerWebsite() {
        const backUrl: string = this.sessionStorageService.getReturnUrl();
        if (backUrl) {
            const result = await this.dialogService.openYesNoDialog(
                this.replaceValueInText(
                    this.translateService.instant("shopping.navigate_back_to_dealer_page_question"),
                    "{dealerName}",
                    this.getSubscriberName())
            );
            if (result)
                HwUtil.redirectWithHistory(backUrl);
        }
    }

    getSubscriberName() {
        if (this.subscriberInfo)
            return this.subscriberInfo.name;
        else
            return '';
    }

    getSelectedAreaName() {
        let text: string;

        if (this.activeStep === 2) {
            if (this.selectedAreaId && this.handleAreas()) {
                let selectedArea = this.areaList.find(x => x.areaId === this.selectedAreaId)
                if (selectedArea) {
                    return selectedArea.language.name;
                }
            }
        } else if (this.activeStep === 3) {
            let text = "";
            if (this.selectedAreaId && this.handleAreas()) {
                let selectedArea = this.areaList.find(x => x.areaId === this.selectedAreaId)
                if (selectedArea) {
                    text = selectedArea.language.name;
                }
            }

            if (this.selectedGroupId && this.handleGroups()) {
                let selectedGroup = this.groupList.find(x => x.ticketGroupId === this.selectedGroupId)
                if (selectedGroup) {
                    text += (text.length > 0 ? ' /  ' : '') + selectedGroup.language.name;
                }
            }

            return text;
        }

        return null;
    }

    getTicketByInformationText(): string[] {
        let ticketSalesForManyDay = this.getSettingValue("TicketSalesForManyDay");
        return [
            `<strong>${this.t.shopping("max_ticket_sales_by_day")}:</strong> ${this.getSettingValue("MaxTicketSalesByDay")}`,

            ...(ticketSalesForManyDay ? [
                `<strong>${this.t.shopping("max_ticket_sales_by_basket")}:</strong> ${this.getSettingValue("MaxTicketSalesByBasket")}`,
            ] : []),

            `<div style="${this.canUserBuyTicketForToday ? '' : 'color:red'}"><strong>${this.t.shopping("ticket_sales_for_actual_day")}:</strong> ${this.getTicketSalesForActualDayText()}</div>`,

            ...(ticketSalesForManyDay ? [
                `<strong>${this.t.shopping("ticket_sales_for_many_day")}</strong>`,
            ] : []),
        ];
    }

    isAreaDisabled(area: AreaResponse): boolean {
        return area.occupancy === 100;
    }

    isGroupDisabled(group: TicketGroupResponse): boolean {
        return false;
    }

    getTicketSalesForActualDayText() {
        if (this.closedForToday)
            return this.t.shopping("not_available");

        if (this.ticketSalesForActualDaySetting.available) {
            if (this.ticketSalesForActualDaySetting.to) {
                return this.replaceValueInText(this.t.shopping("ticket_sales_for_actual_day_time"), "{ticketSalesForActualDay_Time}", this.ticketSalesForActualDaySetting.to)
            }
            else {
                return this.t.shopping("available");
            }
        }
        else {
            return this.t.shopping("not_available");
        }
    }

    emailValueChanged(event: any) {
        return this.personalDataForm?.controls["ConfirmEmail"].updateValueAndValidity();
    }
}

export class Step {
    StepIndex: number;
    StepName: string;
    LogicalIndex: number;
}

export function autocompleteValueValidator(dataset: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const controlValue = control.value;

        if (controlValue) {
            let resp: string[] = dataset.filter(x => x == controlValue);
            if (resp && resp.length > 0)
                return null;
            else
                return { autocompleteValueValidationError: true };
        }

        return null;
    }
}

export function emailMatchValidator(emailAddressFunc): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const controlValue = control.value;
        let emailAddress: string = emailAddressFunc();

        if (controlValue && emailAddress) {
            if (controlValue === emailAddress)
                return null;
            else
                return { emailMatch: true };
        }

        return null;
    }
}
